import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import swal from "sweetalert";
import Loader from "../Common/Loader";
import { useDispatch, useSelector } from "react-redux";
import { useRef } from "react";

import {
  getPathyaAction,
  deletePathyaAction,
} from "../../../store/actions/PathyaActions";

const Pathyas = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const isFirstLoad = useRef(true);

  const pathyaList = useSelector((state) => state.pathya.list);
  const numRows = useSelector((state) => state.pathya.num_rows || 0);
  let searchTimeout = null;

  const [searchQueries, setSearchQueries] = useState({
    pathya_name: "",
    pathya_file: "",
  });

  const [sortField, setSortField] = useState("pathya_name");
  const [sortOrder, setSortOrder] = useState("asc");
  const [currentPage, setCurrentPage] = useState(0);
  const rowsPerPage = 10;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [searchName, setSearchName] = useState("");
  const [searchPdf, setSearchPdf] = useState("");

  // Track active search for server-side filtering
  const [activeSearchField, setActiveSearchField] = useState("");
  const [activeSearchKeyword, setActiveSearchKeyword] = useState("");

  // ================= FETCH =================
  const fetchPathyasFromServer = async (
    search_by = "",
    search_keyword = "",
    pagination = true,
    pageid = 0,
    sort_by = sortField,
    sort_order = sortOrder
  ) => {
    try {
      // ✅ Show loader only on first page load
      if (isFirstLoad.current) {
        setLoading(true);
      }
  
      await dispatch(
        getPathyaAction(
          pagination,
          pageid,
          search_by,
          search_keyword,
          sort_by,
          sort_order
        )
      );
    } catch (err) {
      console.error("Failed to fetch pathyas:", err);
      setError("Failed to load pathya data.");
    } finally {
      setLoading(false);
      isFirstLoad.current = false; // 👈 disable loader forever after first load
    }
  };
  
  useEffect(() => {
    // Check if returning from edit page with state
    if (location?.state?.returnTo) {
      const { currentPage: prevPage, searchQueries: prevSearches, sortField: prevSort, sortOrder: prevOrder } = location.state.returnTo;
      setCurrentPage(prevPage || 0);
      setSearchQueries(prevSearches || {});
      setSortField(prevSort || "pathya_name");
      setSortOrder(prevOrder || "asc");

      // Determine the search field and keyword from restored state
      const searchField = prevSearches?.pathya_name ? "pathya_name" : prevSearches?.pdf_name ? "pdf_name" : "";
      const searchKeyword = prevSearches?.pathya_name || prevSearches?.pdf_name || "";

      setActiveSearchField(searchField);
      setActiveSearchKeyword(searchKeyword);

      // Fetch with restored state
      fetchPathyasFromServer(
        searchField,
        searchKeyword,
        true,
        prevPage || 0,
        prevSort || "pathya_name",
        prevOrder || "asc"
      );
    } else {
      fetchPathyasFromServer();
    }
  }, []);

  // ================= SEARCH =================
  const handleSearch = (e, field) => {
    const keyword = e.target.value;
  
    const clearedQueries = {
      pathya_name: "",
      pathya_file: "",
    };
  
    const updatedQueries = { ...clearedQueries, [field]: keyword };
  
    setSearchQueries(updatedQueries);
    setCurrentPage(0);
    setActiveSearchField(field);
    setActiveSearchKeyword(keyword);
  
    // ✅ debounce API call
    if (searchTimeout) clearTimeout(searchTimeout);
  
    searchTimeout = setTimeout(() => {
      fetchPathyasFromServer(
        field,
        keyword,
        true,
        0,
        sortField,
        sortOrder
      );
    }, 400);
  };
  
  // ================= SORT =================
  const handleSort = (field) => {
    const order = sortField === field && sortOrder === "asc" ? "desc" : "asc";
    setSortField(field);
    setSortOrder(order);
    setCurrentPage(0);
    fetchPathyasFromServer(activeSearchField, activeSearchKeyword, true, 0, field, order);
  };

  // ================= PAGINATION =================
  const totalPages = Math.ceil(numRows / rowsPerPage);
  const startEntry = numRows === 0 ? 0 : currentPage * rowsPerPage + 1;
  const endEntry = Math.min((currentPage + 1) * rowsPerPage, numRows);

  const handlePageChange = (page) => {
    if (page >= 0 && page < totalPages) {
      setCurrentPage(page);
      fetchPathyasFromServer(activeSearchField, activeSearchKeyword, true, page, sortField, sortOrder);
    }
  };

  // ================= DELETE =================
  const handleDelete = (id) => {
    swal({
      title: "Are you sure?",
      text: "This Pathya will be deleted permanently!",
      icon: "warning",
      buttons: true,
      dangerMode: true,
    }).then(async (yes) => {
      if (yes) {
        try {
          setLoading(true);
          await dispatch(deletePathyaAction(id));
          // Refresh the current page after deletion
          await fetchPathyasFromServer(
            activeSearchField,
            activeSearchKeyword,
            true,
            currentPage,
            sortField,
            sortOrder
          );
        } catch (err) {
          setError("Failed to delete pathya");
          console.error("Delete failed:", err);
        } finally {
          setLoading(false);
        }
      }
    });
  };

  return (
    <>
      <div className="row mb-3">
        <div className="col-12 text-end">
          <Link to="/add-pathyas" className="btn btn-primary btn-rounded">
            + Add Pathya
          </Link>
        </div>
      </div>

      <div className="row">
        <div className="col-12">
          <div className="card">
            <div className="card-body">
              {error && <div className="text-danger mb-2">{error}</div>}
              {loading ? (
                <div className="text-center my-5">
                  <Loader size="lg" />
                </div>
              ) : (
                <>
                  <div className="table-responsive">
                    <table className="table table-striped mb-4">
                      <thead className="table-light">
                        <tr>
                          <th onClick={() => handleSort("pathya_name")} style={{ cursor: "pointer" }}>
                            Name{" "}
                            {sortField === "pathya_name" &&
                              (sortOrder === "asc" ? "↑" : "↓")}
                            <input
                              className="form-control form-control-sm mt-1"
                              value={searchQueries.pathya_name}
                              onChange={(e) => handleSearch(e, "pathya_name")}
                              placeholder="Search name"
                              onClick={(e) => e.stopPropagation()}
                            />
                          </th>
                          <th onClick={() => handleSort("pathya_file")} style={{ cursor: "pointer" }}>
                            PDF File{" "}
                            {sortField === "pathya_file" &&
                              (sortOrder === "asc" ? "↑" : "↓")}
                            <input
                              className="form-control form-control-sm mt-1"
                              value={searchQueries.pathya_file}
                              onChange={(e) => handleSearch(e, "pathya_file")}
                              placeholder="Search pdf"
                              onClick={(e) => e.stopPropagation()}
                            />
                          </th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pathyaList?.length > 0 ? (
                          pathyaList.map((item) => (
                            <tr key={item.pathya_id}>
                              <td>{item.pathya_name}</td>
                              <td>{item.pathya_file ? item.pathya_file.split('/').pop() : "-"}</td>
                              <td>
                                <div className="d-flex">
                                  <span
                                    className="edit me-4"
                                    onClick={() =>
                                      navigate("/add-pathyas", {
                                        state: {
                                          item,
                                          returnTo: {
                                            currentPage,
                                            searchQueries,
                                            sortField,
                                            sortOrder,
                                          },
                                        },
                                      })
                                    }
                                    style={{ cursor: "pointer" }}
                                    title="Edit Pathya"
                                  >
                                    <i className="fa fa-pencil-alt text-success"></i>
                                  </span>
                                  <span
                                    className="delete me-2"
                                    onClick={() => handleDelete(item.pathya_id)}
                                    style={{ cursor: "pointer" }}
                                    title="Delete Pathya"
                                  >
                                    <i className="fa fa-trash text-danger"></i>
                                  </span>
                                </div>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="3" className="text-center">
                              No Pathya found.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* PAGINATION */}
                  {numRows > 0 && (
                    <div
                      className="pagination-wrapper"
                      style={{
                        display: "flex",
                        gap: "6px",
                        alignItems: "center",
                        justifyContent: "center",
                        marginTop: "20px",
                        flexWrap: "wrap",
                        flexDirection: "column",
                      }}
                    >
                      <div>
                        Showing {startEntry} to {endEntry} of {numRows} entries
                      </div>
                      <div style={{ display: "flex", gap: "6px", flexWrap: "wrap", justifyContent: "center" }}>
                        <button
                          onClick={() => handlePageChange(Math.max(currentPage - 1, 0))}
                          disabled={currentPage === 0}
                          style={{
                            padding: "6px 12px",
                            border: "1px solid #ddd",
                            borderRadius: "4px",
                            backgroundColor: "#fff",
                            color: "#000",
                            cursor: currentPage === 0 ? "not-allowed" : "pointer",
                            opacity: currentPage === 0 ? 0.5 : 1,
                          }}
                        >
                          &lt;
                        </button>
                        {(() => {
                          const pages = [];
                          const maxPagesToShow = 5;
                          const startPage = Math.max(
                            0,
                            Math.min(currentPage - 2, totalPages - maxPagesToShow)
                          );
                          const endPage = Math.min(startPage + maxPagesToShow, totalPages);

                          if (startPage > 0) {
                            pages.push(
                              <button
                                key={0}
                                onClick={() => handlePageChange(0)}
                                style={{
                                  padding: "6px 12px",
                                  border: "1px solid #ddd",
                                  borderRadius: "4px",
                                  backgroundColor: "#fff",
                                  color: "#000",
                                  cursor: "pointer",
                                }}
                              >
                                1
                              </button>
                            );
                            if (startPage > 1) {
                              pages.push(
                                <span
                                  key="dots1"
                                  style={{ padding: "6px 12px", userSelect: "none" }}
                                >
                                  ...
                                </span>
                              );
                            }
                          }

                          for (let i = startPage; i < endPage; i++) {
                            const isActive = currentPage === i;
                            pages.push(
                              <button
                                key={i}
                                onClick={() => handlePageChange(i)}
                                style={{
                                  padding: "6px 12px",
                                  border: "1px solid #ddd",
                                  borderRadius: "4px",
                                  backgroundColor: isActive ? "#007bff" : "#fff",
                                  color: isActive ? "#fff" : "#000",
                                  cursor: "pointer",
                                }}
                              >
                                {i + 1}
                              </button>
                            );
                          }

                          if (endPage < totalPages) {
                            if (endPage < totalPages - 1) {
                              pages.push(
                                <span
                                  key="dots2"
                                  style={{ padding: "6px 12px", userSelect: "none" }}
                                >
                                  ...
                                </span>
                              );
                            }
                            pages.push(
                              <button
                                key={totalPages - 1}
                                onClick={() => handlePageChange(totalPages - 1)}
                                style={{
                                  padding: "6px 12px",
                                  border: "1px solid #ddd",
                                  borderRadius: "4px",
                                  backgroundColor: "#fff",
                                  color: "#000",
                                  cursor: "pointer",
                                }}
                              >
                                {totalPages}
                              </button>
                            );
                          }

                          return pages;
                        })()}
                        <button
                          onClick={() => handlePageChange(Math.min(currentPage + 1, totalPages - 1))}
                          disabled={currentPage === totalPages - 1}
                          style={{
                            padding: "6px 12px",
                            border: "1px solid #ddd",
                            borderRadius: "4px",
                            backgroundColor: "#fff",
                            color: "#000",
                            cursor: currentPage === totalPages - 1 ? "not-allowed" : "pointer",
                            opacity: currentPage === totalPages - 1 ? 0.5 : 1,
                          }}
                        >
                          &gt;
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Pathyas;